import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { ToastContext } from '@/components/common/CommonToast';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { toastErrorMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { LessonItemType } from '@/types/lesson';
import { Pagination } from '@/types/pagination';
import { MentorLessonColumns } from '@/pages/mentor-lesson-management/table/MentorLessonColumns';

type Props = {
  searchTrigger: number;
};

const MentorLessonList: React.FC<Props> = ({ searchTrigger }) => {
  const [t] = useTranslation('');
  const navigate = useNavigate();
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());

  // No `created_type` / `target_role`: those belong to the admin screen's
  // "who authored the lesson" tabs. A mentor's scope is the lessons granted to
  // the coaches they supervise, which the server resolves on its own.
  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  const { data: lessons, error } = useFetchClient(
    async () => await apiService.lessons.query(query ?? '', params),
    [query, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    lessons?.pagination,
  );

  function handleOpenProgress(rowData: LessonItemType) {
    navigate(
      `${PATH.mentor_lesson_management}/${rowData.id}/progress`,
    );
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  return (
    <CustomDataTable
      wrapClassName="mt-[10px]"
      values={lessons?.data ?? []}
      columns={MentorLessonColumns(t, handleOpenProgress)}
      pagination={paginationComputed}
      onPaginationChanged={onPaginationChanged}
      onSortChanged={onSortChanged}
    />
  );
};

export default MentorLessonList;
